/**
 * Curated dual-host narration scripts — Schedule module pages.
 * Keyed by ListenButton guideId. See index.js for the writing style guide.
 */

export const SCHEDULE_NARRATIONS = {
  'schedule-checklists-help': [
    {
      title: 'The list of lists',
      icon: '📋',
      turns: [
        {
          speaker: 'clara',
          speech: 'Schedulers already live in dates. Sometimes you just need a clean checklist that says: this period is done, and here is who signed it off.',
        },
        {
          speaker: 'liam',
          speech: 'That is Schedule Checklists. Under Schedule you open Checklists and see every fillable list for the job. First up is Financial Period — more list types can join the same menu later.',
        },
      ],
    },
    {
      title: 'Financial Period',
      icon: '💰',
      turns: [
        {
          speaker: 'liam',
          speech: 'You only enter two dates: when the project starts and when it finishes. The portal builds every Monday through Sunday week that covers the job.',
        },
        {
          speaker: 'clara',
          speech: 'The financial period date is always that Monday — the first day of the week. When the books for a week are put away, tick Stored. Stored By and Stored Timestamp stamp themselves.',
        },
      ],
    },
    {
      title: 'Rebuild and the toolkit',
      icon: '🧰',
      turns: [
        {
          speaker: 'clara',
          speech: 'Change the project dates later and hit rebuild. Already stored Mondays that still fall in range stay put. Open weeks outside the new range drop off.',
        },
        {
          speaker: 'liam',
          speech: 'You also get export to C S V, refresh, help with a listen guide, and live viewers so you can see who else is on the page.',
        },
        {
          speaker: 'clara',
          speech: 'Set the job window once, tick stored week by week, and keep closing clean. Happy closing.',
        },
      ],
    },
  ],

  'schedule-model-help': [
    {
      title: 'The model next to the schedule',
      icon: '📦',
      turns: [
        {
          speaker: 'clara',
          speech: 'Schedulers live in Primavera. Detailers live in Navisworks. Everyone else just wants to see the plant. This page is the bridge.',
        },
        {
          speaker: 'liam',
          speech: 'It looks in two places for your three D model: the job SharePoint site, and the Estimates folder for this estimate number. Same page lives under Bid Package, Proposal Schedule, Model when you are still on an estimate. You do not need a job number yet. When it finds a Navisworks or Revit file, you open it from here instead of digging through folders.',
        },
      ],
    },
    {
      title: 'What it finds',
      icon: '🔍',
      turns: [
        {
          speaker: 'liam',
          speech: 'It searches the usual model types: Navisworks N W D and N W C, Revit, I F C, plus exchange formats like G L B and F B X.',
        },
        {
          speaker: 'clara',
          speech: 'On 71940 look under Schedule, Basis of Schedule, 10. 3 D Model. The live N W D is in 03. N W D, the YR Tools zip is in 05. Plugin, and the TimeLiner recording is in 04. A V I. Hit rescan after you drop a new file.',
        },
        {
          speaker: 'liam',
          speech: 'The first N W D that turns up is listed right away. You can prepare or download it while the portal keeps searching SharePoint for the rest.',
        },
        {
          speaker: 'clara',
          speech: 'Come back later and those files are still there instantly. The long SharePoint walk no longer sits in front of the list until it times out.',
        },
      ],
    },
    {
      title: 'View in the portal',
      icon: '🖥️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Hit View in portal on an N W D, N W C, Revit, or I F C file. The portal sends it to Autodesk Platform Services, converts it to a web friendly format, and opens the Autodesk Viewer right here.',
        },
        {
          speaker: 'liam',
          speech: 'Orbit, pan, zoom, section — no desktop Navisworks required on that machine. The first open can take a few minutes while Autodesk translates a large model; after that, opens are quick because we reuse the translation. Download is still there when you need Simulate for TimeLiner or clash.',
        },
      ],
    },
    {
      title: 'P six paints the model',
      icon: '🎨',
      turns: [
        {
          speaker: 'liam',
          speech: 'When you Open a ready model, the side dock reads live Primavera. Green is complete, amber is in progress, slate is not started, red is late against the planned finish. Default is physical percent from P six. Flip to Tracker percent and the same Activity I Ds paint from the field rollup — hours weighted, same as the Monday tracker sync.',
        },
        {
          speaker: 'clara',
          speech: 'The join is Activity I D. Same key the detailer already used in Navisworks with Y R Tools. The first Open can take a while on a big model. We save that match for this job and this Autodesk file, so the next Open paints right away. Press Refresh matching only after a new N W D or a new translate. If the translation has no Activity I D on the items, we tell you and we do not grey the whole plant. Download is then the path into Simulate.',
        },
      ],
    },
    {
      title: 'Planned four D, not as-built',
      icon: '⏱️',
      turns: [
        {
          speaker: 'liam',
          speech: 'The Planned four D control walks planned start and finish only. Enough for a look-ahead. TimeLiner on the desktop is still the place for the full animation — that was already proven, and the A V I sits in the Model folder next to the plugin.',
        },
        {
          speaker: 'clara',
          speech: 'Think of G A Dashboards as the meeting board on a drawing, and this page as the live three D plant with the same Activity I Ds.',
        },
      ],
    },
    {
      title: 'Colour and look-ahead',
      icon: '🔎',
      turns: [
        {
          speaker: 'clara',
          speech: 'Click a legend colour to keep just those pieces. Click a second colour if you want both. The rest of the plant ghosts out.',
        },
        {
          speaker: 'liam',
          speech: 'Turn on the three week look ahead and we keep work that overlaps the Primavera data date plus twenty one days. Finished activities stay out. Late remaining work stays in. Show all clears the chips and brings the plant back.',
        },
      ],
    },
    {
      title: 'Client tools stay doors',
      icon: '🚪',
      turns: [
        {
          speaker: 'liam',
          speech: 'Autodesk Build and Procore in the sidebar are not colour engines. They are bridges into the client world: R F I, drawings, files. Paste their project Home once and jump there.',
        },
        {
          speaker: 'clara',
          speech: 'Still to do: check the Y R Tools source in next to the portal, feed TimeLiner from this same payload, write Activity I D onto items from here, and a mapping health list. Happy coordinating.',
        },
      ],
    },
  ],

  'ga-dashboards-help': [
    {
      title: 'A drawing that tells time',
      icon: '🗺️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Picture a Monday meeting. Instead of scrolling five hundred schedule lines, you put one drawing on the screen: the general arrangement of the plant, every major area lit up in colour and stamped with its dates and labor units.',
        },
        {
          speaker: 'liam',
          speech: 'That is G A Dashboards. It takes the schedule your planner already builds in Primavera P six, and paints start, finish, budget, actual, and remaining units onto the drawing everyone understands.',
        },
        {
          speaker: 'clara',
          speech: 'People used to build this by hand in Excel and PowerPoint, copying dates one by one, every single week. This page does the same thing automatically, and it never goes stale.',
        },
        {
          speaker: 'liam',
          speech: 'For Construction schedule users, this is the default landing when you open a project. One job can have many boards — site plan, stack, modules, air intake — each with its own markers.',
        },
      ],
    },
    {
      title: 'Rev, units, and how data gets in',
      icon: '📅',
      turns: [
        {
          speaker: 'liam',
          speech: 'The toolbar Rev dropdown reads live P six — the working Current copy, plus dated weekly X E R submissions named with the estimate and the date. Every entry shows the project I D, the P six project name, its project codes, and P six\u2019s own data date, so two copies are never a guess. Same idea as Press Progress on the Goodyear job.',
        },
        {
          speaker: 'clara',
          speech: 'You can compare one Rev against another, or against an imported SharePoint snapshot, and see slip days on every balloon. SharePoint sync and manual X E R import are still there as a backup.',
        },
        {
          speaker: 'liam',
          speech: 'Everything on the balloons — dates, physical percent, and labor hours — comes from P six. Progress trackers do not feed this page. Trackers are a different tool; they update Schedule Instances elsewhere.',
        },
        {
          speaker: 'clara',
          speech: 'Percent on a balloon defaults to weighted — actual hours over budget hours across the activities under that pin — or you can average each activity’s physical percent. Labor hours default from resource assignments, because our schedules are resource loaded. Start and finish still come from the activity dates.',
        },
      ],
    },
    {
      title: 'Setting up fast',
      icon: '🎨',
      turns: [
        {
          speaker: 'clara',
          speech: 'New job with an empty list? Use the setup wizard: name the board, drop a P D F or image, then open edit and place your first pin. Or copy a layout from another job — markers and drawing come over, bindings stay as text so you can re-check codes.',
        },
        {
          speaker: 'liam',
          speech: 'You can still create a blank dashboard, pick the drawing from project SharePoint, and drag cards on the list to set your personal order. A long erection sequence P D F is cut into single pages on the server. Scroll the stack and only the slides near you download the first time — after that they stay on this computer for the rest of the day. Bind each slide to a P six activity when the schedule is ready.',
        },
        {
          speaker: 'clara',
          speech: 'Add slides puts a P D F or a picture on the end of the board. Every pin stays on the slide it was placed on.',
        },
        {
          speaker: 'liam',
          speech: 'Replace drawing swaps the whole file. A shorter upload cannot wipe a multi-slide board unless you confirm, and the previous file is kept on the server.',
        },
        {
          speaker: 'clara',
          speech: 'In edit mode, drop a pin, drag a rectangle, or click out a polygon corner by corner. Once you have three corners, the first point turns into a little white target — click it to close the shape, or just double click, or press enter. Escape throws away a half drawn shape. Then bind each marker to a W B S branch or hand-picked activities. The ones already chosen stay in a strip at the top, so you can take one off without scrolling hundreds of lines, or shrink the list to just those. A W B S choice stays pinned the same way. Arrow leaders point from the balloon to the spot on the drawing.',
        },
        {
          speaker: 'liam',
          speech: 'Per marker, toggle what the balloon shows: the area name, dates, percent, units, and optional baseline chips next to the live dates. Busy drawings stay readable when you hide what you do not need. A size slider scales every balloon on the board, and you can size one pin or the whole slide when you are in edit mode.',
        },
        {
          speaker: 'clara',
          speech: 'When a job has a pile of boards, drop them into folders so the list stays tidy. Site plans in one, presentations in another, sequences in a third. Areas of Concern and Press Progress have the same three-dot menu, so you can file those cards too.',
        },
        {
          speaker: 'liam',
          speech: 'Archive tucks a board out of the way without throwing it away. The drawing and pins stay. Restore it from the archived section. Only delete when you truly do not want it back.',
        },
      ],
    },
    {
      title: 'Balloons and colours',
      icon: '🏷️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Balloons sit outside the drawing by default, in the left and right margins, with arrow leaders into the G A. Drag any balloon anytime — you do not need edit mode.',
        },
        {
          speaker: 'liam',
          speech: 'Your layout is remembered per mode. Reset balloons only clears the mode you are looking at. Switch to on drawing or hide them if you prefer. In edit mode you pick a balloon border and fill separately.',
        },
        {
          speaker: 'clara',
          speech: 'A new pin starts as Area one until you bind it. Then the title becomes the activity name, or the W B S name if you bound a whole branch. You can still rename any balloon yourself.',
        },
        {
          speaker: 'clara',
          speech: 'Grey is not started, amber is in progress, green is done, red is late — past planned finish or negative float.',
        },
        {
          speaker: 'liam',
          speech: 'A letter A after a date means it is an actual. Budget, actual, and remaining units sit under the dates when that toggle is on.',
        },
      ],
    },
    {
      title: 'Presenting like a pro',
      icon: '🎬',
      turns: [
        {
          speaker: 'clara',
          speech: 'The time slider scrubs the project like a movie. Hit play and areas light up week by week. Open the timeline strip for a mini Gantt under the drawing.',
        },
        {
          speaker: 'liam',
          speech: 'Compare flags slip days against another Rev or snapshot. Present for fullscreen. Export a P N G of the slide on screen, or a P D F of every slide. The download asks if you want the company header, data date, legend, and portal link — or a plain drawing with none of that.',
        },
        {
          speaker: 'clara',
          speech: 'That is the tour. Set up zones once, tune the balloons, pick the Rev you are presenting, and let the schedule do the talking every week after. Happy planning.',
        },
      ],
    },
    {
      title: 'Press Progress on seven five nine nine eight',
      icon: '▦',
      turns: [
        {
          speaker: 'liam',
          speech: 'On the Goodyear job, you will also see a Press Progress card. That is the dense grid of every press — rows J through N, units ten down to one.',
        },
        {
          speaker: 'clara',
          speech: 'Toggle Overall for cumulative mechanical, electrical, piping, punch list, and T C C C. Switch to Last Week to see only what moved since the previous P six update. Present it or export a P N G for the meeting.',
        },
      ],
    },
  ],

  'areas-of-concern-help': [
    {
      title: 'The email, as a one-pager',
      icon: '✉',
      turns: [
        {
          speaker: 'clara',
          speech: 'Every week the schedule update email carries an Areas of Concern section — the risks, holds, and asks that the room needs to hear.',
        },
        {
          speaker: 'liam',
          speech: 'This page is that body. Goodyear uses Engineering, Procurement, Construction, Interfaces, and Change Management. Napanee and new jobs use Assumptions and Needs — same shape as the email to Enzo.',
        },
      ],
    },
    {
      title: 'Third-party dates, not pours',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'The dates on the Needs list come from Current remaining Finish for anyone who is not L G L — foundations, deliveries, access, mechanical, anywhere in the W B S. Not last week’s submitted X E R, and not the portal snapshot.',
        },
        {
          speaker: 'liam',
          speech: 'On every job we read the live P six Current working copy — the same project you filter for responsible party not L G L. Incomplete remaining finish always becomes the Need date, even if it moved. A completed actual finish is the one case we will not pull an emailed access or curing date backward. Activity I Ds land in Extra intel so you can check them before you send.',
        },
      ],
    },
    {
      title: 'Red means new',
      icon: '🔴',
      turns: [
        {
          speaker: 'clara',
          speech: 'Turn on Diff colors and anything new versus last week shows in red. Items that dropped off last week stay visible with a strikethrough so the room sees what expired.',
        },
        {
          speaker: 'liam',
          speech: 'Draft from last week copies the prior body into a new week. Refresh stays on this week — it keeps the attachment names and bullets you already edited, then re-reads P six. Goodyear also pulls notebooks, the Master Control Chart Excel for pending N C C numbers, and open R F Is.',
        },
      ],
    },
    {
      title: 'Keep every week',
      icon: '📅',
      turns: [
        {
          speaker: 'liam',
          speech: 'Use the week dropdown the same way as Press Progress. Each week is a Y Y Y Y M M D D key that lines up with the P six dated revision.',
        },
        {
          speaker: 'clara',
          speech: 'Edit the bullets, Publish when the email goes out, and Copy email when you want the plain text body. Outlook mailbox read is not wired yet — paste those priority emails into Extra intel.',
        },
      ],
    },
  ],

  'press-progress-help': [
    {
      title: 'One page, fifty presses',
      icon: '▦',
      turns: [
        {
          speaker: 'clara',
          speech: 'This board is the Goodyear press scoreboard. Fifty presses in five rows — J, K, L, M, N — each showing mechanical, electrical, piping, punch list, and T C C C.',
        },
        {
          speaker: 'liam',
          speech: 'It replaces the Excel one-pager that used to be rebuilt by hand. The numbers come straight from Primavera: actual hours over budget hours for the three trades, and physical percent for punch list.',
        },
      ],
    },
    {
      title: 'Overall versus last week',
      icon: '📅',
      turns: [
        {
          speaker: 'liam',
          speech: 'Overall is the cumulative picture from the latest dated schedule update.',
        },
        {
          speaker: 'clara',
          speech: 'Last Week is the gain — this update minus the previous one — so a meeting can focus on what actually moved, not the whole job history.',
        },
        {
          speaker: 'liam',
          speech: 'Hit Present for fullscreen, or export a P N G for the slide deck. Refresh any time after a new P six submission lands.',
        },
      ],
    },
  ],

  'estimate-lbs-help': [
    {
      title: 'Locations on a timeline',
      icon: '🗺',
      turns: [
        {
          speaker: 'clara',
          speech: 'This is a location-based schedule for an estimate — each pipe rack is a row, and the weeks march across the top. Same idea as the old Nemaska pipe-rack L B S spreadsheet, but live from P six.',
        },
        {
          speaker: 'liam',
          speech: 'Open it under Estimation, Bid Package, Proposal Schedule, L B S Schedule after you pick the estimate in the header. There is no job number yet, so it lives with the estimate, not under a project.',
        },
      ],
    },
    {
      title: 'Phases and revisions',
      icon: '📅',
      turns: [
        {
          speaker: 'liam',
          speech: 'Week cells use short phase codes — structure assembly, piping assembly, tray and electrical, then ready to ship — weighted from the ironworker, piping, and electrical budgets on each rack.',
        },
        {
          speaker: 'clara',
          speech: 'The Rev picker defaults to the live Current copy. When schedulers drop a new dated X E R into P six, that revision shows up here automatically — same refresh pattern as Press Progress on seventy-five nine nine eight.',
        },
        {
          speaker: 'liam',
          speech: 'Present for the meeting wall, or export a P N G. Refresh any time after a schedule update lands.',
        },
      ],
    },
  ],

  'proposal-schedules-help': [
    {
      title: 'A schedule before there is a job',
      icon: '📋',
      turns: [
        {
          speaker: 'clara',
          speech: 'Most schedules in this portal live under a job number after award. Proposal Schedule is different — it belongs to the estimate while the bid is still being built, so it lives under Estimation, not under a project.',
        },
        {
          speaker: 'liam',
          speech: 'Switch to the Estimation department, select an estimate in the header, then open Proposal Schedule. The menu stays hidden until that estimate is picked, because there is no job yet.',
        },
      ],
    },
    {
      title: 'Where the draft comes from',
      icon: '🧩',
      turns: [
        {
          speaker: 'liam',
          speech: 'It learns W B S style from the earliest dated P six copies for similar estimates — the first couple of drafts, not only the ones with a P in the name.',
        },
        {
          speaker: 'clara',
          speech: 'Hours and activities come from Cost Breakdown. SharePoint files under that estimate folder — scope of work, drawings, cost sheets — are inventoried as source docs and help nudge the sequence.',
        },
        {
          speaker: 'liam',
          speech: 'You also get a schedule narrative, like a short execution plan, so later you remember what we assumed and why.',
        },
      ],
    },
    {
      title: 'Excel, XER, and P six',
      icon: '📤',
      turns: [
        {
          speaker: 'clara',
          speech: 'The portal never writes into the Primavera database. When you are happy, download Excel in the same layout as the Monday morning P six upload, or grab an X E R.',
        },
        {
          speaker: 'liam',
          speech: 'Import that into a new proposal project in P six — never overwrite a working copy. Refresh the draft after Cost Breakdown or SharePoint changes, and the old version is kept as superseded.',
        },
      ],
    },
    {
      title: 'Teach it with the feedback chat',
      icon: '💬',
      turns: [
        {
          speaker: 'clara',
          speech: 'This page is private to Project Controls for now — just Behzad and Yousef. On the right you will see the Schedule coach chatbot. Tell it what is wrong: sequence, W B S names, durations, whatever you would fix by hand.',
        },
        {
          speaker: 'liam',
          speech: 'Every message is saved. Preferred rules become learnings — global for every future estimate, or only for this bid if you say so. Say apply or regenerate when you want a new revision that uses those learnings.',
        },
        {
          speaker: 'clara',
          speech: 'Pick an estimate, review the draft, chat the corrections, download when it looks right. Happy bidding.',
        },
      ],
    },
  ],

  'schedule-msp-export-help': [
    {
      title: 'The dirty save',
      icon: '📅',
      turns: [
        {
          speaker: 'clara',
          speech: 'Clients still ask for an M S Project copy of the Primavera schedule. The old way was to save the P six file under a new extension, then sit there checking every start, every finish, and every link.',
        },
        {
          speaker: 'liam',
          speech: 'That save is dirty. M S Project likes to recalculate. A Saturday start slides to Monday. A finish to start with lag quietly becomes something else. That is why this page exists.',
        },
      ],
    },
    {
      title: 'Dates and logic stay locked',
      icon: '🔒',
      turns: [
        {
          speaker: 'liam',
          speech: 'Pick the weekly X E R from the job SharePoint site — that is the file that already went out. Or use live P six if that revision still has activities. Or upload an X E R from your desk.',
        },
        {
          speaker: 'clara',
          speech: 'Convert writes the same interchange P six already uses. Every activity is manually scheduled from the P six clock. Predecessor type and lag copy across. If a date or a link does not match, the page says so and will not file the pack.',
        },
      ],
    },
    {
      title: 'What the client gets',
      icon: '📄',
      turns: [
        {
          speaker: 'clara',
          speech: 'You download two files. The M S Project XML opens in M S Project. Save as M P P if they want the binary. The landscape P D F uses the company letterhead for this job number, with bars drawn from the P six dates.',
        },
        {
          speaker: 'liam',
          speech: 'Save to SharePoint drops both into the same week folder as the P six X E R, using the Novelis style name. Convert, check the green match, file it. Happy handing over.',
        },
      ],
    },
  ],

  'schedule-analysis-help': [
    {
      title: 'The delay story, not just bars',
      icon: '📉',
      turns: [
        {
          speaker: 'clara',
          speech: 'Primavera already scores health. What the portal did not have is a delay register from the notebooks: why the finish moved, and who owns it.',
        },
        {
          speaker: 'liam',
          speech: 'Schedule Analysis is that page. It is Yousef only for now. Open it on a job, hit Run full analysis, and it reads dated notebook lines, compares every weekly copy, and scores D C M A fourteen.',
        },
      ],
    },
    {
      title: 'Overview first, evidence under the tabs',
      icon: '📊',
      turns: [
        {
          speaker: 'clara',
          speech: 'The Overview is the meeting brief: what slipped, which window moved the finish, and whether the path was ours or a third party.',
        },
        {
          speaker: 'liam',
          speech: 'Events, Windows, and Health hold the proof. Chat only answers from those stored facts. It will not invent a date.',
        },
      ],
    },
    {
      title: 'Data Standard: the codes decide, not the name',
      icon: '🏷️',
      turns: [
        {
          speaker: 'clara',
          speech: 'The Data Standard tab lists every Primavera file of the estimate with its five project codes: responsible party, scope, status, main calendar and package. The file name means nothing any more. The codes say what a file is.',
        },
        {
          speaker: 'liam',
          speech: 'One file per party is coded current. Run Monday check now scores only those: D C M A fourteen plus the data standard. Accepted or returned, with the reason. The same check mails itself out every Monday at six. Happy reading the delay, not guessing it.',
        },
      ],
    },
  ],

  'execution-plan-help': [
    {
      title: 'The field one pager',
      icon: '📋',
      turns: [
        {
          speaker: 'clara',
          speech: 'A schedule tells you when. The field still needs a one page story: where the crane stands, where steel lands, where you preassemble, and in what order you pick.',
        },
        {
          speaker: 'liam',
          speech: 'That is Execution Plan, under Schedule. It is private to Project Controls for now — just Behzad and Yousef. Pick a date and you are writing that day\'s story, not a forever document.',
        },
      ],
    },
    {
      title: 'P six and SharePoint',
      icon: '🔗',
      turns: [
        {
          speaker: 'liam',
          speech: 'The page reads Primavera live for this job. The revision list is the same Current plus dated copies you already know. It hunts for crane, laydown, preassembly, and sequence language so the coach has dates to lean on.',
        },
        {
          speaker: 'clara',
          speech: 'Then you attach the real paper. Upload from your computer, or walk the job SharePoint site and pick a drawing or method statement. The coach reads those files. It should not invent a crane pad that is not in the documents.',
        },
      ],
    },
    {
      title: 'Chat, save, send',
      icon: '✉️',
      turns: [
        {
          speaker: 'clara',
          speech: 'Ask it to generate the one pager. Correct it in the chat, or type straight into the page. Save keeps a draft for that date. Publish date marks the version you are willing to share.',
        },
        {
          speaker: 'liam',
          speech: 'Download gives you a P D F with the right company letterhead for this job number. Email sends that same branded file. Change the date and you get a new copy — last Tuesday stays last Tuesday.',
        },
        {
          speaker: 'clara',
          speech: 'Attach the drawings, let P six set the clock, write the story, send it. Happy lifting.',
        },
      ],
    },
  ],
};
